import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Edit, Save, RotateCcw, Settings, Play, Square, Pause, 
  ArrowRight, Zap, Bell, BarChart2, TrendingUp, TrendingDown, ListFilter, 
  Workflow, LayoutDashboard, Copy, Download, Upload, Eye, EyeOff, 
  TestTube, CheckCircle, XCircle, AlertTriangle, Clock, User, Mail, 
  Phone, Calendar, FileText, Hash, Link, Database, Server, Globe
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const WorkflowBuilder = () => {
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [activeTab, setActiveTab] = useState('builder'); // 'builder', 'templates', 'testing', 'monitoring'
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [workflowConnections, setWorkflowConnections] = useState([]);
  const [draggedStep, setDraggedStep] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    category: 'ticket_processing',
    status: 'draft'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workflowsResponse, templatesResponse, logsResponse] = await Promise.all([
        MockDataService.getWorkflowsNew(),
        MockDataService.getWorkflowTemplatesNew(),
        MockDataService.getWorkflowExecutionLogs()
      ]);

      if (workflowsResponse.success) {
        setWorkflows(workflowsResponse.data);
      } else {
        setError(workflowsResponse.message);
      }
      if (templatesResponse.success) {
        setTemplates(templatesResponse.data);
      }
      if (logsResponse.success) {
        setExecutionLogs(logsResponse.data);
      }
    } catch (err) {
      setError('Failed to fetch workflow data.');
      console.error('Error fetching workflow data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async () => {
    try {
      const response = await MockDataService.createWorkflow(newWorkflow);
      if (response.success) {
        setWorkflows([...workflows, response.data]);
        setShowCreateForm(false);
        setNewWorkflow({ name: '', description: '', category: 'ticket_processing', status: 'draft' });
      }
    } catch (err) {
      setError('Failed to create workflow.');
    }
  };

  const handleUpdateWorkflow = async (workflowId, workflowData) => {
    try {
      const response = await MockDataService.updateWorkflow(workflowId, workflowData);
      if (response.success) {
        setWorkflows(workflows.map(w => w.id === workflowId ? response.data : w));
        setEditingWorkflow(null);
      }
    } catch (err) {
      setError('Failed to update workflow.');
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    try {
      const response = await MockDataService.deleteWorkflow(workflowId);
      if (response.success) {
        setWorkflows(workflows.filter(w => w.id !== workflowId));
      }
    } catch (err) {
      setError('Failed to delete workflow.');
    }
  };

  const handleTestWorkflow = async (workflowId) => {
    setIsTesting(true);
    try {
      const response = await MockDataService.testWorkflow(workflowId, {});
      if (response.success) {
        setTestResults(response.data);
      }
    } catch (err) {
      setError('Failed to test workflow.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSelectWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setWorkflowSteps(workflow.steps || []);
    setWorkflowConnections(workflow.connections || []);
  };

  const handleAddStep = (type) => {
    const newStep = {
      id: `step_${Date.now()}`,
      name: `New ${type}`,
      type: type,
      position: { x: 200 + (workflowSteps.length * 200), y: 100 },
      config: {}
    };
    setWorkflowSteps([...workflowSteps, newStep]);
  };

  const handleDeleteStep = (stepId) => {
    setWorkflowSteps(workflowSteps.filter(s => s.id !== stepId));
    setWorkflowConnections(workflowConnections.filter(c => c.from !== stepId && c.to !== stepId));
  };

  const handleConnectSteps = (fromStepId, toStepId) => {
    const newConnection = { from: fromStepId, to: toStepId };
    setWorkflowConnections([...workflowConnections, newConnection]);
  };

  const handleSaveWorkflow = () => {
    if (selectedWorkflow) {
      const updatedWorkflow = {
        ...selectedWorkflow,
        steps: workflowSteps,
        connections: workflowConnections
      };
      handleUpdateWorkflow(selectedWorkflow.id, updatedWorkflow);
    }
  };

  const getStepIcon = (type) => {
    const icons = {
      trigger: Zap,
      action: Play,
      condition: ListFilter,
      delay: Clock,
      notification: Bell
    };
    return icons[type] || Play;
  };

  const getStepColor = (type) => {
    const colors = {
      trigger: 'bg-blue-500',
      action: 'bg-green-500',
      condition: 'bg-yellow-500',
      delay: 'bg-purple-500',
      notification: 'bg-orange-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-600 bg-green-100',
      draft: 'text-yellow-600 bg-yellow-100',
      inactive: 'text-gray-600 bg-gray-100',
      failed: 'text-red-600 bg-red-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
        <Workflow className="mr-3" size={28} />
        Workflow Builder
      </h1>
      <p className="text-gray-700 mb-8">
        Create visual workflows for ticket processing automation with drag-and-drop interface.
      </p>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        {[
          { id: 'builder', label: 'Workflow Builder', icon: Workflow },
          { id: 'templates', label: 'Templates', icon: Copy },
          { id: 'testing', label: 'Testing', icon: TestTube },
          { id: 'monitoring', label: 'Monitoring', icon: BarChart2 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="mr-2" size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Workflow Builder Tab */}
      {activeTab === 'builder' && (
        <div className="space-y-6">
          {/* Workflow List */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Workflows</h2>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
              >
                <Plus className="mr-2" size={16} />
                Create Workflow
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workflows.map(workflow => (
                <div
                  key={workflow.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedWorkflow?.id === workflow.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelectWorkflow(workflow)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(workflow.status)}`}>
                      {workflow.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{workflow.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Version {workflow.version}</span>
                    <span>{workflow.steps?.length || 0} steps</span>
                  </div>
                  <div className="flex space-x-2 mt-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestWorkflow(workflow.id);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={isTesting}
                    >
                      <TestTube size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingWorkflow(workflow);
                      }}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteWorkflow(workflow.id);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Workflow Designer */}
          {selectedWorkflow && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Visual Designer: {selectedWorkflow.name}
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveWorkflow}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
                  >
                    <Save className="mr-2" size={16} />
                    Save Workflow
                  </button>
                  <button
                    onClick={() => handleTestWorkflow(selectedWorkflow.id)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
                    disabled={isTesting}
                  >
                    <TestTube className="mr-2" size={16} />
                    {isTesting ? 'Testing...' : 'Test Workflow'}
                  </button>
                </div>
              </div>

              {/* Step Palette */}
              <div className="mb-6 p-4 bg-white rounded-lg border">
                <h3 className="font-semibold text-gray-900 mb-3">Add Steps</h3>
                <div className="flex space-x-2">
                  {[
                    { type: 'trigger', label: 'Trigger', icon: Zap },
                    { type: 'action', label: 'Action', icon: Play },
                    { type: 'condition', label: 'Condition', icon: ListFilter },
                    { type: 'delay', label: 'Delay', icon: Clock },
                    { type: 'notification', label: 'Notification', icon: Bell }
                  ].map(stepType => (
                    <button
                      key={stepType.type}
                      onClick={() => handleAddStep(stepType.type)}
                      className="flex items-center px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <stepType.icon className="mr-2" size={16} />
                      {stepType.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Workflow Canvas */}
              <div className="relative bg-white border-2 border-dashed border-gray-300 rounded-lg h-96 overflow-auto">
                <div className="p-4">
                  {workflowSteps.map(step => {
                    const IconComponent = getStepIcon(step.type);
                    return (
                      <div
                        key={step.id}
                        className={`absolute ${getStepColor(step.type)} text-white p-3 rounded-lg cursor-move min-w-32`}
                        style={{ left: step.position.x, top: step.position.y }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <IconComponent size={16} className="mr-2" />
                            <span className="text-sm font-medium">{step.name}</span>
                          </div>
                          <button
                            onClick={() => handleDeleteStep(step.id)}
                            className="text-white hover:text-red-200 ml-2"
                          >
                            <XCircle size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Workflow Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <div key={template.id} className="bg-white p-4 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">
                      {template.complexity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                    <span>{template.steps} steps</span>
                    <span>{template.estimatedTime}</span>
                  </div>
                  <button className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700">
                    Use Template
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Testing Tab */}
      {activeTab === 'testing' && (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Workflow Testing</h2>
            
            {testResults && (
              <div className="bg-white p-4 border rounded-lg mb-4">
                <h3 className="font-semibold text-gray-900 mb-3">Test Results</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{testResults.status}</div>
                    <div className="text-sm text-gray-600">Status</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{testResults.executionTime}ms</div>
                    <div className="text-sm text-gray-600">Execution Time</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{testResults.steps.length}</div>
                    <div className="text-sm text-gray-600">Steps Executed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{testResults.logs.length}</div>
                    <div className="text-sm text-gray-600">Log Entries</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-gray-900">Execution Logs:</h4>
                  {testResults.logs.map((log, index) => (
                    <div key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center text-gray-500">
              Select a workflow and click "Test Workflow" to see results here.
            </div>
          </div>
        </div>
      )}

      {/* Monitoring Tab */}
      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Workflow Execution Monitoring</h2>
            
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workflow</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Steps</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {executionLogs.map(log => (
                    <tr key={log.id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{log.workflowName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.ticketId}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.duration}ms</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.stepsExecuted}/{log.stepsExecuted + log.stepsFailed}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.triggeredBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create Workflow Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Workflow</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newWorkflow.name}
                  onChange={(e) => setNewWorkflow({...newWorkflow, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newWorkflow.description}
                  onChange={(e) => setNewWorkflow({...newWorkflow, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={newWorkflow.category}
                  onChange={(e) => setNewWorkflow({...newWorkflow, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ticket_processing">Ticket Processing</option>
                  <option value="escalation">Escalation</option>
                  <option value="feedback">Feedback</option>
                  <option value="notification">Notification</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorkflow}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Create Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilder;
